import { useEffect, useState } from 'react'

/**
 * Pre-submit mirror of the backend's password rules
 * (`backend/crates/domain/src/password.rs`) so a field that reads valid is
 * guaranteed to submit clean; the backend stays the authority. The deny-list
 * is the backend's own data file, lazily loaded as its own small chunk.
 */
export const PASSWORD_MAX_LEN = 128
/** Every restaurant account, whatever its role. */
export const PASSWORD_MIN_RESTAURANT = 12
export const PASSWORD_MIN_PLATFORM = 16

/** NFKC then code points — matches the backend's char counting. */
export function passwordLength(plain: string): number {
  return [...plain.normalize('NFKC')].length
}

let commonPasswords: Set<string> | undefined

/** Exported for tests; pages go through `useCommonPasswords`. */
export async function loadCommonPasswords(): Promise<Set<string>> {
  if (!commonPasswords) {
    const { default: raw } = await import('@backend-data/common-passwords.txt?raw')
    commonPasswords = new Set(raw.split('\n').filter(Boolean))
  }
  return commonPasswords
}

/** The deny-list once loaded; `undefined` while the chunk is in flight. */
export function useCommonPasswords(): Set<string> | undefined {
  const [list, setList] = useState(commonPasswords)
  useEffect(() => {
    if (!list) void loadCommonPasswords().then(setList)
  }, [list])
  return list
}

/** The backend lowercases after NFKC; the entries are stored lowercased. */
export function isCommonPassword(plain: string, list: Set<string>): boolean {
  return list.has(plain.normalize('NFKC').toLowerCase())
}

export interface PasswordProblem {
  key: 'error.PASSWORD_TOO_SHORT' | 'error.PASSWORD_TOO_LONG' | 'error.PASSWORD_TOO_COMMON'
  params: Record<string, number>
}

/**
 * First rule the password breaks, in the backend's order; `null` means the
 * submitting will pass. With no list yet the common check is skipped — the
 * server still catches it.
 */
export function passwordProblem(
  plain: string,
  min: number,
  list: Set<string> | undefined,
): PasswordProblem | null {
  const length = passwordLength(plain)
  if (length < min) return { key: 'error.PASSWORD_TOO_SHORT', params: { min, length } }
  if (length > PASSWORD_MAX_LEN) return { key: 'error.PASSWORD_TOO_LONG', params: { max: PASSWORD_MAX_LEN } }
  if (list && isCommonPassword(plain, list)) return { key: 'error.PASSWORD_TOO_COMMON', params: {} }
  return null
}
